import {
  Button,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from '@material-ui/core';
import * as React from 'react';
import { useState } from 'react';

const DEFAULT_MAX_VALUES = 500;

export type BulkListValue = number | string;
export type BulkListValueType = 'email' | 'number';
export type BulkListSource = 'csv' | 'text';

export interface BulkListParseOptions {
  acceptedHeaders?: string[];
  maxValues?: number;
  source: BulkListSource;
  valueType: BulkListValueType;
}

export interface BulkListParseResult {
  error: string | null;
  values: BulkListValue[];
}

interface Props {
  acceptedHeaders?: string[];
  disabled?: boolean;
  label: string;
  maxValues?: number;
  onChange: (values: BulkListValue[], error: string | null) => void;
  valueType: BulkListValueType;
}

const normalizeHeader = (value: string): string =>
  value.trim().toLowerCase().replace(/[\s-]+/g, '_');

const removeCsvQuotes = (value: string): string => {
  const trimmedValue = value.trim();

  if (trimmedValue.startsWith('"') && trimmedValue.endsWith('"')) {
    return trimmedValue.slice(1, -1).replace(/""/g, '"').trim();
  }

  return trimmedValue;
};

const getRawValues = (input: string, source: BulkListSource): BulkListParseResult => {
  const content = input.replace(/^\uFEFF/, '');

  if (source === 'csv') {
    const rows = content.split(/\r?\n/).filter(row => row.trim().length > 0);
    const rowWithMultipleColumns = rows.find(row => row.includes(','));

    if (rowWithMultipleColumns) {
      return {
        error: 'CSV files must contain exactly one column.',
        values: [],
      };
    }

    return {
      error: null,
      values: rows.map(removeCsvQuotes),
    };
  }

  return {
    error: null,
    values: content
      .split(/[,\r\n]+/)
      .map(value => value.trim())
      .filter(value => value.length > 0),
  };
};

export const parseBulkList = (
  input: string,
  options: BulkListParseOptions,
): BulkListParseResult => {
  const maxValues = options.maxValues ?? DEFAULT_MAX_VALUES;
  const rawResult = getRawValues(input, options.source);

  if (rawResult.error) return rawResult;

  const rawValues = rawResult.values as string[];
  const acceptedHeaders = (options.acceptedHeaders || []).map(normalizeHeader);

  if (
    rawValues.length > 0
    && acceptedHeaders.includes(normalizeHeader(rawValues[0]))
  ) {
    rawValues.shift();
  }

  const invalidValues: string[] = [];
  const parsedValues: BulkListValue[] = [];
  const seenValues = new Set<string>();

  rawValues.forEach(rawValue => {
    let parsedValue: BulkListValue = rawValue;
    let distinctKey = rawValue;

    if (options.valueType === 'number') {
      const numericValue = Number(rawValue);

      if (!/^\d+$/.test(rawValue) || !Number.isSafeInteger(numericValue) || numericValue <= 0) {
        invalidValues.push(rawValue);
        return;
      }

      parsedValue = numericValue;
      distinctKey = numericValue.toString();
    } else {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rawValue)) {
        invalidValues.push(rawValue);
        return;
      }

      distinctKey = rawValue.toLowerCase();
    }

    if (!seenValues.has(distinctKey)) {
      seenValues.add(distinctKey);
      parsedValues.push(parsedValue);
    }
  });

  if (invalidValues.length > 0) {
    const preview = invalidValues.slice(0, 3).join(', ');
    const remainingCount = invalidValues.length - 3;

    return {
      error: `Invalid ${options.valueType === 'number' ? 'ID' : 'email'} value${invalidValues.length === 1 ? '' : 's'}: ${preview}${remainingCount > 0 ? ` and ${remainingCount} more` : ''}.`,
      values: parsedValues,
    };
  }

  if (parsedValues.length > maxValues) {
    return {
      error: `Lists may contain no more than ${maxValues} distinct values.`,
      values: parsedValues,
    };
  }

  return { error: null, values: parsedValues };
};

export const BulkListInput: React.FC<Props> = (props: Props) => {
  const [fileName, setFileName] = useState<string>('');
  const [input, setInput] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const inputId = `bulk-list-${props.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  const parseInput = (value: string, source: BulkListSource) => {
    const result = parseBulkList(value, {
      acceptedHeaders: props.acceptedHeaders,
      maxValues: props.maxValues,
      source,
      valueType: props.valueType,
    });

    setError(result.error);
    props.onChange(result.values, result.error);
    return result;
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInput(value);
    setFileName('');
    parseInput(value, 'text');
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';

    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      const fileError = 'Please upload a .csv file.';
      setFileName(file.name);
      setError(fileError);
      props.onChange([], fileError);
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => {
      const fileError = 'The selected file could not be read.';
      setFileName(file.name);
      setError(fileError);
      props.onChange([], fileError);
    };
    reader.onload = () => {
      const contents = typeof reader.result === 'string' ? reader.result : '';
      const result = parseInput(contents, 'csv');
      setFileName(file.name);
      setInput(result.error ? contents : result.values.join('\n'));
    };
    reader.readAsText(file);
  };

  return (
    <FormControl fullWidth error={Boolean(error)}>
      <Typography>{props.label}</Typography>
      <Typography variant="caption">
        Use the textbox to paste values or upload a CSV file.
      </Typography>
      <TextField
        disabled={props.disabled}
        fullWidth
        multiline
        onChange={handleTextChange}
        placeholder="Paste comma-separated values or enter one value per line"
        rows={6}
        value={input}
        variant="outlined"
      />
      <input
        accept=".csv,text/csv"
        disabled={props.disabled}
        hidden
        id={inputId}
        onChange={handleFileChange}
        type="file"
      />
      <Typography align="left" style={{ margin: '8px 0' }}>
        <strong>OR</strong>
      </Typography>
      <label htmlFor={inputId}>
        <Button
          component="span"
          color="primary"
          disabled={props.disabled}
          variant="outlined">
          Upload CSV
        </Button>
      </label>
      {fileName && <FormHelperText>Selected file: {fileName}</FormHelperText>}
      <FormHelperText error={Boolean(error)}>
        {error || <strong>Maximum {props.maxValues ?? DEFAULT_MAX_VALUES} distinct values.</strong>}
      </FormHelperText>
    </FormControl>
  );
};
